/**
 * Frontend error reporting (TIMR-12593).
 *
 * Captures uncaught JavaScript errors in the browser and forwards them to the backend
 * ({@code POST /log/error}), which turns them into GCP Error Reporting events under the
 * service {@code timrweb-frontend}. Sent with a plain {@code fetch} (the endpoint is CSRF-exempt):
 * strictly fire-and-forget, never surfaces a dialog on failure, and {@code keepalive} lets a report
 * still go out during page unload.
 */

type ClientErrorKind = "onerror" | "unhandledrejection" | "react";

interface ClientErrorReport {
	name?: string;
	message: string;
	stack?: string;
	pageUrl: string;
	frontendVersion?: string;
}

// The build version the browser is running, imprinted server-side into the page. A stale tab keeps its old
// value, so the report shows which frontend raised the error even when the backend has since moved on. (The
// commit lives in the timr-commit meta tag for debugging via view-source, but is not reported.)
function metaContent(name: string): string | undefined {
	return document.querySelector(`meta[name="${name}"]`)?.getAttribute("content") ?? undefined;
}

// De-duplicate identical errors so a render loop throwing the same error every frame reports it once.
const reportedKeys = new Set<string>();

/**
 * Only errors that demonstrably involve our own code are reported. An error whose stack trace and
 * source file reference foreign URLs only (browser extensions, injected wallet providers like
 * TronLink, third-party scripts) is outside our control and must not end up in Error Reporting.
 * Errors carrying no URL at all (e.g. promise rejections with string reasons, stackless
 * DOMExceptions) are kept — they may well originate from our code.
 */
export function isOwnSourceError(stack: string | undefined, sourceFile: string | undefined, origin: string): boolean {
	const source = `${stack ?? ""}\n${sourceFile ?? ""}`;
	if (!source.includes("://")) {
		return true;
	}
	return source.includes(`${origin}/`);
}

/**
 * Detects errors "muted" by the browser's cross-origin policy. When a script loaded from another
 * origin (without {@code crossorigin="anonymous"} + CORS headers) throws, the HTML spec requires the
 * browser to hide all details from {@code window.onerror}: the handler receives only the literal
 * message "Script error." — no error object, no source file, no position. Such reports carry zero
 * diagnostic value and stem from scripts outside our control (browser extensions, injected
 * third-party code), so they are not reported.
 */
export function isMaskedCrossOriginError(message: string, error: unknown, sourceFile: string | undefined): boolean {
	return error == null && !sourceFile && (message === "Script error." || message === "Script error");
}

/**
 * Firefox and Safari format stack frames as {@code name@url:line:col}, while GCP Error Reporting only
 * recognizes V8's {@code    at name (url:line:col)} form and shows anything else as unparsed raw text.
 * Rewrites such frames to the V8 form, per line, so mixed input is safe. Firefox marks anonymous
 * nested closures with {@code /<} in the frame name (e.g. {@code initChart/</</<} is an anonymous
 * function three levels inside {@code initChart}); the markers are dropped — the position already
 * identifies the frame. Header lines, V8 frames, and frames without a position (Safari's
 * {@code fn@[native code]}) pass through unchanged.
 */
export function normalizeStackFrames(stack: string): string {
	return stack
		.split("\n")
		.map((line) => {
			const frame = /^(.*)@(.+:\d+:\d+)$/.exec(line);
			if (!frame) {
				return line;
			}
			const name = frame[1].replace(/(\/?<)+$/, "");
			return name ? `    at ${name} (${frame[2]})` : `    at ${frame[2]}`;
		})
		.join("\n");
}

/**
 * Builds the report message for a promise rejection whose reason is not an Error (so it carries no
 * stack). A string reason is used verbatim. The common object case is a jqXHR escaping via
 * {@code await}, whose response status is the actionable part (e.g. {@code 404 error}). Anything else
 * falls back to a bare label.
 */
export function describeRejectionReason(reason: unknown): string {
	if (typeof reason === "string") {
		return reason;
	}
	if (reason !== null && typeof reason === "object") {
		const {status, statusText} = reason as Record<string, unknown>;
		const detail = [status, statusText]
			.filter((value) => typeof value === "string" || typeof value === "number")
			.map(String)
			.filter((value) => value.length > 0)
			.join(" ");
		if (detail) {
			return `Unhandled promise rejection: ${detail}`;
		}
	}
	return "Unhandled promise rejection";
}

function reportClientError(kind: ClientErrorKind, name: string | undefined, message: string, rawStack: string | undefined, sourceFile?: string): void {
	if (!isOwnSourceError(rawStack, sourceFile, window.location.origin)) {
		return;
	}

	const stack = rawStack === undefined ? undefined : normalizeStackFrames(rawStack);
	const dedupeKey = `${kind}|${message}|${stack ?? ""}`;
	if (reportedKeys.has(dedupeKey)) {
		return;
	}
	reportedKeys.add(dedupeKey);

	const body: ClientErrorReport = {
		name, message, stack,
		pageUrl: window.location.href,
		frontendVersion: metaContent("timr-version"),
	};
	void fetch("/timr/log/error", {
		method: "POST",
		headers: {"Content-Type": "application/json"},
		body: JSON.stringify(body),
		credentials: "same-origin",
		keepalive: true,
	}).catch(() => {
		// Swallow: reporting a failed error report would risk a feedback loop.
	});
}

/** Installs global handlers for uncaught errors and unhandled promise rejections. Called once from main.ts. */
export function installGlobalErrorReporting(): void {
	window.addEventListener("error", (event: ErrorEvent) => {
		const error = event.error as Error | undefined;
		if (isMaskedCrossOriginError(event.message, error, event.filename)) {
			return;
		}
		reportClientError("onerror", error?.name, error?.message ?? event.message, error?.stack, event.filename);
	});

	window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
		const reason: unknown = event.reason;
		if (reason instanceof Error) {
			reportClientError("unhandledrejection", reason.name, reason.message, reason.stack);
		} else {
			reportClientError("unhandledrejection", undefined, describeRejectionReason(reason), undefined);
		}
	});
}

/** Reports an error caught by a React error boundary, enriched with the component stack. */
export function reportReactError(error: Error, componentStack?: string): void {
	const stack = componentStack ? `${error.stack ?? ""}\n\nComponent stack:${componentStack}` : error.stack;
	reportClientError("react", error.name, error.message, stack);
}
